import type { BatteryInfo, DeviceInfo } from "@capacitor/device";
import { Device } from "@capacitor/device";
import { Capacitor } from "@capacitor/core";
import { Network } from "@capacitor/network";
import {
  DeviceTrackingNative,
  isDeviceTrackingNativeAvailable,
} from "../plugins/deviceTrackingNative";
import {
  APP_ABOUT_TO_CLOSE_EVENT,
  APP_AUTO_LAUNCHED_EVENT,
} from "./appLifecycleScheduler";
import axiosInstance, { getActiveRoleId } from "./axios";
import {
  getDecryptedToken,
  getDecryptedUserData,
  getLoggedInUserId,
} from "./bycrypt";
import { API_KEY, API_URL, getDeviceId, ROLE } from "./utils";
import {
  consumePageRefreshMarker,
  isPageRefreshInFlight,
} from "./pageRefreshMarker";

/**
 * Memo of {@link isRefreshContinuation} — resolved once per document.
 */
let refreshContinuation = false;
let refreshContinuationResolved = false;

/**
 * True when the CURRENT document was reached by `location.reload()` rather than by a
 * real launch — i.e. the previous document was a pull-to-refresh, a version update or a
 * chunk-recovery reload, NOT the app closing and opening again.
 *
 * Deliberately keyed ONLY on the marker `markPageRefresh()` writes (sessionStorage
 * survives a reload and dies with the WebView), and not on the Navigation Timing entry,
 * because the decision has to stay CONSISTENT with the `pagehide` handler of the
 * document we just replaced — and that handler can only see the marker.
 *
 * Getting the two sides out of step is worse than reporting a refresh as an open/close
 * pair: suppressing only the close (or only the start) leaves `last_start` and
 * `last_closed` describing an app state that never existed.
 */
function isRefreshContinuation(): boolean {
  if (refreshContinuationResolved) return refreshContinuation;
  refreshContinuationResolved = true;
  // One-shot: consumed here, so a later genuine launch of this same browsing context is
  // tracked normally again.
  refreshContinuation = consumePageRefreshMarker();
  if (refreshContinuation) {
    console.log(
      "[DeviceTracking] Document was reloaded, not relaunched - keeping the previous open, reporting no new start",
    );
  }
  return refreshContinuation;
}

/**
 * deviceTracking — reports the attendance tablet's app OPEN / CLOSE moments to the
 * backend (`POST /device-tracking`) so the auto start / auto close feature can be
 * verified from the admin "Restart App History" screen.
 *
 * What counts as an open / close (never a plain foreground / background):
 *   - OPEN  → a real process launch. JavaScript booting IS a fresh process (the killed
 *             app was launched by the user or by the schedule), so the start is reported
 *             on boot, after a schedule driven `autoLaunched`, and when the tablet user
 *             logs in. Returning to the foreground is NOT reported — and while the last
 *             reported state is "start" no further start is sent: ONE open = ONE start.
 *             EXCEPTION: a page REFRESH (`location.reload()` — pull-to-refresh, version
 *             update, chunk recovery) replaces the document but NOT the process, so it
 *             is neither an open nor a close; see {@link isRefreshContinuation}.
 *   - CLOSE → the process really ending: the in-app Exit / Restart action (the request is
 *             awaited before the process dies), the schedule driven auto close
 *             (`aboutToClose`, which has a grace period) and any other termination
 *             detected natively (`MainActivity.onDestroy`).
 *             A background state or a device lock while the app stays open is NOT a close
 *             — BUT a background that is followed by a kill the app never survives
 *             (force stop, OOM kill, reboot) is detected on the next launch via the
 *             native *tentative* close, which is stored on every background transition
 *             and dropped again on resume.
 *
 * Exactly-once guarantee (one open → one start, one close → one request at most):
 *   - `lastSentState` gates duplicate start / close signals inside a session;
 *   - `noteCloseSending` + the native-first gate in `closeGuardingNative` stop the
 *     WebView and the native termination hook from both POSTing the same close;
 *   - the flush compares against BOTH the JS and the native "last reported" markers,
 *     so a close the server already holds is never replayed.
 *
 * Attendance-tablet role on ANDROID ONLY (nothing else may be tracked):
 *   - every JS start / close passes `isDeviceTrackingAllowed()` (Android native build
 *     AND the ACTIVE attendance-tablet role); web and iOS are never tracked — iOS has
 *     no native counterpart any more, so the whole feature is Android specific;
 *   - native is ARMED only when an attendance start is really sent
 *     (`pushReportContext({ enabled: true })`) and DISARMED again by
 *     `syncNativeTrackingContext` on every non-attendance boot / login — and native
 *     additionally gates its own storage (tentative / pending / crash close) on that
 *     flag, so a session of any other role leaves no trace in the tracking data.
 *
 * Contract (see `backend/module/deviceTracking/controller.ts`):
 *   - exactly ONE of `lastStart` / `lastClosed` per request;
 *   - the timestamp is an ISO-8601 UTC instant (`new Date().toISOString()`) — the
 *     absolute moment, so the device time zone can never shift it;
 *   - one document per `deviceId` (upsert).
 *
 * A close that could not be sent while the process was dying (swipe away, force stop)
 * is persisted natively / locally and reported on the next launch with its ORIGINAL time.
 * On Android that next-launch report happens FIRST natively in `MainActivity.onCreate`
 * (before the WebView boots); the JavaScript flush below waits for it (`flushing`) and
 * only retries if native could not deliver it.
 *
 * OFFLINE ("closed with no internet, reopened with no internet, network back later"):
 *   - a moment that could NOT be delivered is stored in localStorage with its ORIGINAL
 *     time — {@link LS_PENDING_CLOSE_KEY} for a close, {@link LS_PENDING_START_KEY} for
 *     an open — so no open / close is ever lost while the tablet is offline;
 *   - stored moments are replayed on the next launch, as soon as connectivity comes
 *     back (the DOM `online` event, plus the Capacitor `networkStatusChange` event — the
 *     reliable one inside an Android WebView), and every {@link RETRY_INTERVAL_MS} while
 *     something is still owed, as a safety net — see {@link initDeviceTracking};
 *   - on Android the native side owns the close half of this: a ConnectivityManager
 *     callback re-delivers a close stored while offline the moment the network is back,
 *     even when no WebView is alive (see `DeviceTrackingPlugin.armNetworkRetry`).
 */

const DEVICE_TRACKING_ENDPOINT = "/device-tracking";

/** A pending close within this window of the last reported close is that same close. */
const CLOSE_DEDUPE_MS = 90_000;
/**
 * Native has stored a close within this window → it is THIS close (the Android
 * `onDestroy` hook runs before the WebView is torn down, so native usually records the
 * close first): JavaScript must then defer to native instead of sending a second POST.
 */
const NATIVE_CLOSE_HANDLED_MS = 10_000;
/**
 * How long a JavaScript close POST counts as "still in flight" for the native side
 * (see `noteCloseSending`) — native stores the moment but fires no second POST.
 */
const CLOSE_SEND_WINDOW_MS = 10_000;

/** Epoch millis (as string) of a close still owed to the API — web / non-native fallback. */
const LS_PENDING_CLOSE_KEY = "device_tracking_pending_close_at";
/**
 * Epoch millis (as string) of an OPEN still owed to the API.
 *
 * The start needs exactly the same offline safety net as the close: the attendance
 * tablet is often launched by the schedule on a site with no connectivity yet, and
 * that open moment must still reach the API once the network is back — with its
 * ORIGINAL time, never with the time it finally got delivered.
 */
const LS_PENDING_START_KEY = "device_tracking_pending_start_at";
/** Epoch millis (as string) of the last close JavaScript reported. */
const LS_REPORTED_CLOSE_KEY = "device_tracking_last_reported_close_at";

/**
 * Safety-net retry cadence while something is still owed to the API. The `online` /
 * `networkStatusChange` events normally fire long before this — it only covers the case
 * where the connection came back without the WebView being told (common on Android,
 * where the process can be frozen in the background while the radio reconnects).
 */
const RETRY_INTERVAL_MS = 30_000;

/**
 * Last state that was reported.
 *
 * CLOSE — a close is only ever sent directly after an "open", so the extra close
 * signals that trail one close (pagehide right after `aboutToClose`, or a
 * pending-close flush on the next launch) can never push the close to a later, wrong
 * moment, and a background state / device lock (which are never reported) can never be
 * mistaken for a close.
 *
 * START — while the last reported state is "start" no second start is sent: one open
 * = exactly one start, no matter how often boot / `autoLaunched` / the login effect /
 * a `user` update fire within the same session. A failed start resets the state so a
 * later `trackDeviceStart()` can retry (the gate at enqueue time still prevents two
 * concurrent sends).
 */
let lastSentState: "start" | "close" | null = null;
/**
 * True once a LIVE close (not a flush of a previous session) was reported: the app is
 * going down right now, so no further start may be sent from this session — a start
 * signal arriving in that window (a `user` update, the login effect) would be a phantom
 * open AND would clear native's close-dedupe marker far too early.
 */
let sessionClosed = false;
/** Serialises requests so a close arriving during a start is never dropped. */
let queue: Promise<unknown> = Promise.resolve();

/**
 * The open moment THIS session tried to report (0 until the first start signal here).
 *
 * Used to tell apart two very similar replays of a stored offline start:
 *  - replaying THIS session's own start (launched offline, network came back later) must
 *    mark the session as reported, so a trailing login / `user` signal does not send a
 *    second start for the very same open;
 *  - replaying an OLDER, previous session's start must NOT mark anything, or the live
 *    start of the fresh session would be gated away and that open would be lost.
 */
let sessionStartAtMs = 0;

/** Handle of the background retry timer (null = not scheduled). */
let retryTimer: ReturnType<typeof setTimeout> | null = null;

/** Absolute UTC instant — never affected by the device time zone. */
const nowIso = (): string => new Date().toISOString();

function toMillis(iso: string): number {
  const value = Date.parse(iso);
  return Number.isNaN(value) ? Date.now() : value;
}

// --------------------------------------------------------------- persistence ---

function readLocalPendingCloseMs(): number {
  try {
    return Number(localStorage.getItem(LS_PENDING_CLOSE_KEY) || 0) || 0;
  } catch {
    return 0;
  }
}

function setLocalPendingCloseMs(atMs: number): void {
  try {
    localStorage.setItem(LS_PENDING_CLOSE_KEY, String(atMs));
  } catch {
    /* storage unavailable */
  }
}

function clearLocalPendingClose(deliveredMs?: number): void {
  try {
    if (deliveredMs !== undefined) {
      const stored =
        Number(localStorage.getItem(LS_PENDING_CLOSE_KEY) || 0) || 0;
      // A newer close was stored while this POST was in flight — it is still owed, so
      // leave it alone. (Matches the tentative-vs-reported check native does natively.)
      if (stored && stored !== deliveredMs) return;
    }
    localStorage.removeItem(LS_PENDING_CLOSE_KEY);
  } catch {
    /* storage unavailable */
  }
}

/**
 * The open moment that still has to reach the API (the app was launched while the
 * tablet had no connectivity), or 0 when there is none.
 *
 * Deliberately NOT age-capped: the backend keeps a single `last_start` per device and
 * this slot only ever holds the most recent UNDELIVERED open, so a week-old value means
 * the tablet really has been offline for a week — dropping it would lose the very data
 * this offline path exists to preserve.
 */
function readLocalPendingStartMs(): number {
  try {
    return Number(localStorage.getItem(LS_PENDING_START_KEY) || 0) || 0;
  } catch {
    return 0;
  }
}

function setLocalPendingStartMs(atMs: number): void {
  try {
    localStorage.setItem(LS_PENDING_START_KEY, String(atMs));
  } catch {
    /* storage unavailable */
  }
}

/**
 * Drops the stored open moment — but ONLY if it is still the moment this send actually
 * delivered. A flush that fired while this POST was in flight can have written a newer
 * value in the meantime; blindly removing the key would lose that newer open.
 *
 * @param deliveredMs the exact timestamp that reached the API (omitted only when
 *                    dropping a stale value no send is tracking — then removal is safe).
 */
function clearLocalPendingStart(deliveredMs?: number): void {
  try {
    if (deliveredMs !== undefined) {
      const stored =
        Number(localStorage.getItem(LS_PENDING_START_KEY) || 0) || 0;
      if (stored && stored !== deliveredMs) return;
    }
    localStorage.removeItem(LS_PENDING_START_KEY);
  } catch {
    /* storage unavailable */
  }
}

function readLastReportedCloseMs(): number {
  try {
    return Number(localStorage.getItem(LS_REPORTED_CLOSE_KEY) || 0) || 0;
  } catch {
    return 0;
  }
}

/** Close state held by the native side: what is still owed + what it saw confirmed. */
interface NativeCloseState {
  /** A close the next launch has to report (real close, or the background fallback). */
  pendingMs: number;
  /** The last close the native side saw delivered to the API (0 = never). */
  reportedMs: number;
  /**
   * Android: the native next-launch flush (`MainActivity.onCreate`) is POSTing right
   * now — JavaScript must wait for it instead of sending the same close again.
   */
  flushing: boolean;
}

async function readNativeCloseState(): Promise<NativeCloseState> {
  let pendingMs = 0;
  let reportedMs = 0;
  let flushing = false;
  if (isDeviceTrackingNativeAvailable()) {
    try {
      const result = await DeviceTrackingNative.getPendingClose();
      if (result?.hasPending && result.at > pendingMs) pendingMs = result.at;
      if (result?.lastReportedAt && result.lastReportedAt > reportedMs) {
        reportedMs = result.lastReportedAt;
      }
      flushing = result?.flushing === true;
    } catch (error) {
      console.warn("[DeviceTracking] getPendingClose failed:", error);
    }
  }
  return { pendingMs, reportedMs, flushing };
}

async function clearPendingClose(atMs?: number): Promise<void> {
  clearLocalPendingClose(atMs);
  if (isDeviceTrackingNativeAvailable()) {
    try {
      await DeviceTrackingNative.clearPendingClose();
    } catch (error) {
      console.warn("[DeviceTracking] clearPendingClose failed:", error);
    }
  }
}

/** Remembers that this close was reported (native de-duplicates its own termination hook). */
async function markCloseReported(atMs: number): Promise<void> {
  try {
    localStorage.setItem(LS_REPORTED_CLOSE_KEY, String(atMs));
  } catch {
    /* storage unavailable */
  }
  if (isDeviceTrackingNativeAvailable()) {
    try {
      await DeviceTrackingNative.markCloseReported({ at: atMs });
    } catch (error) {
      console.warn("[DeviceTracking] markCloseReported failed:", error);
    }
  }
}

/**
 * True only when the logged-in user holds the attendance-tablet role.
 * Mirrors `resolveAttendanceTabletDevice()` in `appLifecycleScheduler` — only that
 * device role runs the kiosk screen, so only that device is tracked.
 */
export function isAttendanceTabletDevice(): boolean {
  try {
    const userData: any = getDecryptedUserData();
    if (!userData) return false;
    const roleKey = userData?.role?.key || "";
    const roleName = String(userData?.role?.name || "").toLowerCase();
    return (
      roleKey === ROLE.ATTENDANCE_TABLET ||
      roleKey === "attendance-tablet" ||
      roleName.includes("attendance tablet") ||
      roleName.includes("attendance-tablet")
    );
  } catch {
    return false;
  }
}

/**
 * App open / close tracking runs ONLY inside the Android app.
 *
 * The whole feature is Android specific (the native close hook, the schedule driven
 * auto close, the kiosk deployment), so neither the web build nor iOS may ever report
 * a moment — iOS has no native counterpart any more and the JavaScript posts are
 * blocked here as well.
 */
export function isDeviceTrackingPlatform(): boolean {
  try {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";
  } catch {
    return false;
  }
}

/**
 * The ONE gate every start / close report passes: ANDROID native build AND the
 * attendance-tablet role. Anything else — web, iOS, any other role, logged out —
 * must leave no trace in the tracking data.
 */
export function isDeviceTrackingAllowed(): boolean {
  return isDeviceTrackingPlatform() && isAttendanceTabletDevice();
}

async function collectDeviceInfo(): Promise<DeviceInfo | undefined> {
  try {
    return await Device.getInfo();
  } catch (error) {
    console.warn("[DeviceTracking] Device.getInfo() failed:", error);
    return undefined;
  }
}

async function collectBatteryInfo(): Promise<BatteryInfo | undefined> {
  try {
    const info = await Device.getBatteryInfo();
    // iOS (notably the SIMULATOR) reports batteryLevel = -1 when the level is
    // unknown — sending that sentinel would store it and show "-100%" in the
    // admin Restart App History. The Capacitor contract for a real level is
    // [0, 1] (Android `level/scale`, web `navigator.getBattery().level`), so
    // anything outside that range is dropped; charging state is kept as long
    // as it is a real boolean.
    const level = info?.batteryLevel;
    const batteryLevel =
      typeof level === "number" &&
      Number.isFinite(level) &&
      level >= 0 &&
      level <= 1
        ? level
        : undefined;
    const isCharging =
      typeof info?.isCharging === "boolean" ? info.isCharging : undefined;
    // Nothing usable → omit batteryInfo entirely from the payload / native context.
    if (batteryLevel === undefined && isCharging === undefined)
      return undefined;
    return { batteryLevel, isCharging };
  } catch (error) {
    console.warn("[DeviceTracking] Device.getBatteryInfo() failed:", error);
    return undefined;
  }
}

/**
 * Hands native everything it needs to POST the close itself at the real close moment
 * (used when the WebView gets no usable hook — swipe away / OS kill). Refreshed on every
 * start so the token / active role stay current.
 */
async function pushReportContext(): Promise<void> {
  if (!isDeviceTrackingNativeAvailable() || !isDeviceTrackingAllowed()) return;
  try {
    const [deviceId, deviceInfo, batteryInfo] = await Promise.all([
      getDeviceId(),
      collectDeviceInfo(),
      collectBatteryInfo(),
    ]);
    const token = getDecryptedToken();
    await DeviceTrackingNative.setReportContext({
      enabled: true,
      url: `${API_URL}${DEVICE_TRACKING_ENDPOINT}`,
      apiKey: API_KEY || "",
      authorization: token ? `Bearer ${token}` : "",
      activeRole: getActiveRoleId() || "",
      deviceId,
      userId: getLoggedInUserId() || "",
      // Spreading matters: `DeviceInfo` / `BatteryInfo` are interfaces, and interfaces
      // have no implicit index signature, so `deviceInfo || {}` alone does not satisfy
      // the `Record<string, unknown>` field type (TS2322) under CRA's type checker.
      deviceInfo: deviceInfo ? { ...deviceInfo } : {},
      batteryInfo: batteryInfo ? { ...batteryInfo } : {},
    });
    nativeLog("Report context pushed - native can now POST a close itself");
  } catch (error) {
    console.warn("[DeviceTracking] setReportContext failed:", error);
    nativeLog(
      `setReportContext failed: ${(error as Error)?.message || String(error)}`,
    );
  }
}

/**
 * Keeps native armed ONLY for attendance-tablet sessions (requirement: open / close
 * tracking applies to the attendance-tablet role and nothing else).
 *
 * Native arming ({@link pushReportContext}) happens when an attendance start is really
 * sent — but nothing ever disarmed it again, so a later NON-attendance session on the
 * same install would have been tracked natively with the stale context. This sync runs
 * after every pending-close flush (boot, login, `autoLaunched`) and:
 *
 *   - attendance session → no-op (the start path arms native with fresh credentials);
 *   - anything else (other role, logged out, no session yet) → pushes
 *     `enabled: false` with EMPTY credentials, so native stores nothing and POSTs
 *     nothing from here on. The pending / tentative copies are deliberately NOT
 *     cleared: they are attendance-origin closes still owed to the API and are
 *     flushed BEFORE this sync runs (see {@link trackDeviceStart}); the native
 *     Android boot flush also runs before JavaScript even exists.
 */
async function syncNativeTrackingContext(): Promise<void> {
  if (!isDeviceTrackingNativeAvailable()) return;
  if (isAttendanceTabletDevice()) return;
  try {
    await DeviceTrackingNative.setReportContext({
      enabled: false,
      url: "",
      apiKey: "",
      authorization: "",
      activeRole: "",
      deviceId: "",
      userId: "",
      deviceInfo: {},
      batteryInfo: {},
    });
    nativeLog(
      "Not an attendance-tablet session - native close tracking disarmed",
    );
  } catch (error) {
    console.warn("[DeviceTracking] setReportContext (disarm) failed:", error);
  }
}

/**
 * Mirrors a line into the native log (logcat) under the
 * `DeviceTracking` tag — a fallback so the open / close reports JavaScript sends also
 * show up in the native log next to the plugin's own lines.
 * Fire and forget — never throws into the caller.
 */
function nativeLog(message: string): void {
  if (!isDeviceTrackingNativeAvailable()) return;
  try {
    void DeviceTrackingNative.log({ message }).catch(() => undefined);
  } catch {
    /* native logger not available */
  }
}

/** Builds the payload and POSTs it to the device-tracking endpoint. */
async function performSend(
  state: "start" | "close",
  atIso: string,
): Promise<boolean> {
  try {
    const [deviceId, deviceInfo, batteryInfo] = await Promise.all([
      getDeviceId(),
      collectDeviceInfo(),
      collectBatteryInfo(),
    ]);

    const userId = getLoggedInUserId();

    const payload: Record<string, unknown> = {
      deviceId,
      deviceInfo: deviceInfo ? { ...deviceInfo } : undefined,
      batteryInfo: batteryInfo ? { ...batteryInfo } : undefined,
      userId: userId || undefined,
    };

    // Exactly one timestamp per request — the backend rejects both / neither.
    if (state === "start") payload.lastStart = atIso;
    else payload.lastClosed = atIso;

    await axiosInstance.post(DEVICE_TRACKING_ENDPOINT, payload);
    console.log(`[DeviceTracking] Reported app ${state} (${atIso})`);
    nativeLog(
      `Reported app ${state} (${atIso}) -> ${API_URL}${DEVICE_TRACKING_ENDPOINT}`,
    );
    return true;
  } catch (error) {
    console.warn(`[DeviceTracking] Failed to report app ${state}:`, error);
    nativeLog(
      `Failed to report app ${state} (${atIso}): ${
        (error as Error)?.message || String(error)
      }`,
    );
    return false;
  }
}

/**
 * Queues a start / close report. No-ops when the device is not an attendance tablet.
 *
 * @param state "start" when the app was opened, "close" when it was closed.
 * @param atIso absolute UTC instant of the moment (defaults to now) — used to flush a
 *              pending close from a previous session with its ORIGINAL close time.
 */
export function requestDeviceTracking(
  state: "start" | "close",
  atIso?: string,
): Promise<void> {
  if (!isDeviceTrackingAllowed()) return Promise.resolve();

  const at = atIso || nowIso();
  const atMs = toMillis(at);

  if (state === "close") {
    // Only an "open" may be followed by a "close": this suppresses the duplicate close
    // signals that trail one live close and guarantees that a background state / device
    // lock — which are never reported — can not be mistaken for a close. A close with
    // an explicit timestamp is a persisted replay, not a duplicate live signal; it must
    // remain retryable after an offline POST fails.
    if (lastSentState === "close" && !atIso) return Promise.resolve();
    lastSentState = "close";
    // Persisted BEFORE anything else: the process may die at any moment from here on.
    setLocalPendingCloseMs(atMs);
    if (atIso) {
      // A flush replays a close of a PREVIOUS session — at boot there is no native POST
      // in flight, so it must not be diverted by the native-first guard, and it must
      // NOT close THIS session (the boot start has to follow it).
      return enqueueCloseSend(at, atMs);
    }
    // A live close = the app is going down right now.
    sessionClosed = true;
    return closeGuardingNative(at, atMs);
  }

  // One open = exactly one start:
  //  - while the last reported state is "start" (no close in between) every further
  //    start signal — boot + autoLaunched + the login effect + any `user` update — is a
  //    duplicate of the SAME open and is dropped here;
  //  - after a live close the session is over: any start signal left in the dying
  //    session would be a phantom open, so it is dropped as well.
  if (lastSentState === "start" || sessionClosed) return Promise.resolve();
  // The launch moment of THIS session. Remembered once and reused by every retry of the
  // same open, so a boot that failed offline and a later login signal both keep the
  // original launch time instead of drifting forward with each attempt.
  if (!sessionStartAtMs) sessionStartAtMs = atMs;
  lastSentState = "start";
  // Persisted BEFORE the request: the tablet may have been launched with no connectivity
  // at all. Without this the open moment was simply thrown away on a failed POST and was
  // never reported — now it survives until the network is back (see flushPendingStart).
  // The backend keeps ONE `last_start` per device, so keeping only the most recent
  // unsent open matches exactly what the server would have stored anyway.
  setLocalPendingStartMs(sessionStartAtMs);
  // Only reached when this start is really sent: refresh the native report context
  // (so native can POST a close even when the WebView is gone) — which also clears
  // native's cross-session close-dedupe marker AFTER the pending flush above ran.
  if (isDeviceTrackingNativeAvailable()) void pushReportContext();

  return enqueueStartSend(
    new Date(sessionStartAtMs).toISOString(),
    sessionStartAtMs,
    { isSessionStart: true },
  );
}

/**
 * Queues the actual start POST (serialised, clears the pending copy on success).
 *
 * `isSessionStart` marks a start that IS this session's own open — either the live
 * signal or its later offline replay. Only then does a successful delivery update the
 * session state, so a trailing login / `user` signal can not send a second start for the
 * same open. A replay of an OLDER session's start leaves the state untouched: this
 * session still owes its own start, and gating it away would lose that open.
 */
function enqueueStartSend(
  at: string,
  atMs: number,
  options?: { isSessionStart?: boolean },
): Promise<void> {
  const isSessionStart = options?.isSessionStart === true;
  queue = queue.then(async () => {
    const delivered = await performSend("start", at);
    if (delivered) {
      // The server now holds this open moment - drop the stored copy.
      clearLocalPendingStart(atMs);
      // Only mark the session as reported while it is not already past a close: a
      // replay landing after `aboutToClose` / `pagehide` must not turn "close" back into
      // "start" — that would let a trailing duplicate close signal through the gate.
      if (isSessionStart && lastSentState !== "close") {
        lastSentState = "start";
      }
    } else {
      // Allow a retry of the SAME session's open. Deliberately keyed on
      // `isSessionStart`: a failed replay of an OLDER session's open must never clear a
      // state this session already reached, or it would un-gate a duplicate start for
      // the open we are currently in. The stored copy stays, so the online /
      // connectivity / timer flush still delivers this exact moment later on.
      if (isSessionStart && lastSentState === "start") lastSentState = null;
      scheduleOfflineFlush();
    }
  });
  return queue.then(() => undefined);
}

/**
 * JavaScript is about to POST a close. Two guards keep ONE close from being sent twice
 * when the native hook and the WebView race each other:
 *
 *  1. native stored this close a moment ago (Android records it in `onDestroy` BEFORE
 *     the WebView is torn down, so native usually wins) → defer to native, which is
 *     already POSTing it or will flush it on the next launch;
 *  2. otherwise tell native this close is in flight (`noteCloseSending`) — a later
 *     native hook then stores the moment but fires no second POST.
 */
async function closeGuardingNative(at: string, atMs: number): Promise<void> {
  if (isDeviceTrackingNativeAvailable()) {
    try {
      const native = await DeviceTrackingNative.getPendingClose();
      if (
        native?.hasPending &&
        Math.abs(Date.now() - native.at) < NATIVE_CLOSE_HANDLED_MS
      ) {
        // Native owns this close: it POSTs it right away or flushes it on next launch.
        nativeLog("Close already recorded natively - deferring to native");
        return;
      }
      void DeviceTrackingNative.noteCloseSending({
        until: Date.now() + CLOSE_SEND_WINDOW_MS,
      }).catch(() => undefined);
    } catch (error) {
      console.warn("[DeviceTracking] native close guard failed:", error);
    }
  }
  await enqueueCloseSend(at, atMs);
}

/** Queues the actual close POST (serialised, clears the pending copies on success). */
function enqueueCloseSend(at: string, atMs: number): Promise<void> {
  queue = queue.then(async () => {
    const delivered = await performSend("close", at);
    if (delivered) {
      // The server now holds this close moment: mark it reported (so native does not
      // store it again) and drop the pending copy.
      await markCloseReported(atMs);
      await clearPendingClose(atMs);
    } else {
      // Still owed: the stored copy survives (setLocalPendingCloseMs ran before this),
      // so keep retrying — but ONLY while the app is alive. A close that happens as the
      // process dies never reaches this branch: the process is gone and Android native
      // owns the retry from there (armNetworkRetry).
      scheduleOfflineFlush();
    }
  });
  return queue.then(() => undefined);
}

/** Guards against two concurrent boot flushes racing each other. */
let flushing: Promise<void> | null = null;

/**
 * Reports a close left over from a previous session (the process died before the request
 * could be sent — swipe away, force stop, an offline exit) with its ORIGINAL time.
 * A pending close that matches a close already reported is simply discarded.
 */
async function flushPendingClose(): Promise<void> {
  let localPendingMs = readLocalPendingCloseMs();
  let native = await readNativeCloseState();

  // Android runs a NATIVE next-launch flush from MainActivity.onCreate — it can report
  // this close BEFORE this WebView has even booted. Wait for it to settle instead of
  // racing it with a second request for the same close (native flags itself `flushing`).
  if (native.flushing) {
    nativeLog("Native next-launch flush is running - waiting for it to settle");
    for (let i = 0; i < 25 && native.flushing; i += 1) {
      await new Promise((resolve) => setTimeout(resolve, 400));
      localPendingMs = readLocalPendingCloseMs();
      native = await readNativeCloseState();
    }
    if (native.flushing) {
      // Still busy after ~10s: native owns this close — it either delivers it or the
      // stored copy survives for the next launch. Never send a second request here.
      nativeLog("Native flush still running - leaving this close to native");
      return;
    }
  }

  const atMs = Math.max(localPendingMs, native.pendingMs);
  if (!atMs) return;

  // Both stores count: JS and native each mark what they saw delivered, so a close the
  // server already holds (e.g. native's immediate POST that finished right before the
  // process died) is dropped instead of being POSTed a second time on this launch.
  const reportedMs = Math.max(readLastReportedCloseMs(), native.reportedMs);
  if (reportedMs > 0 && Math.abs(atMs - reportedMs) < CLOSE_DEDUPE_MS) {
    // Same close, already on the server (native stored it after JS reported it).
    console.log(
      "[DeviceTracking] Pending close already reported - dropping the duplicate",
    );
    nativeLog("Pending close already reported - dropping the duplicate");
    await clearPendingClose(atMs);
    return;
  }

  const atIso = new Date(atMs).toISOString();
  console.log(
    `[DeviceTracking] Flushing close from the previous session (${atIso})`,
  );
  nativeLog(`Flushing close from the previous session (${atIso})`);
  await requestDeviceTracking("close", atIso);
}

function flushPendingCloseOnce(): Promise<void> {
  if (flushing) return flushing;
  flushing = flushPendingClose()
    .catch((error) =>
      // Never let a failed flush swallow the start that follows it — the pending copy
      // stays stored, and the cache reset below lets a later trackDeviceStart retry.
      console.error("[DeviceTracking] pending close flush failed:", error),
    )
    .finally(() => {
      flushing = null;
    });
  return flushing;
}

/**
 * Reports an OPEN that could not be delivered when it happened (the tablet was launched
 * with no connectivity) with its ORIGINAL launch time — the exact gap this closes: the
 * start used to be dropped on a failed POST and was never reported at all.
 *
 * Runs on the next launch, on the app returning to the foreground and as soon as the
 * network is back. It goes straight to {@link enqueueStartSend} instead of through
 * `requestDeviceTracking`, so the one-open-one-start gate (which is about LIVE signals of
 * this session) can not swallow a moment that really happened in an earlier session.
 */
async function flushPendingStart(): Promise<void> {
  const atMs = readLocalPendingStartMs();
  if (!atMs) return;

  const atIso = new Date(atMs).toISOString();
  console.log(
    `[DeviceTracking] Flushing the app start missed while offline (${atIso})`,
  );
  nativeLog(`Flushing the app start missed while offline (${atIso})`);
  // `isSessionStart` only when this IS the open this session already tried to report
  // (launched offline, network came back later): then a successful delivery must stop a
  // trailing login / `user` signal from sending a second start for the same open. A
  // stored start from an OLDER session must NOT gate this session's own start below.
  const isSessionStart = sessionStartAtMs > 0 && atMs === sessionStartAtMs;
  await enqueueStartSend(atIso, atMs, { isSessionStart });
}

/**
 * Is anything still owed to the API? Used to keep the retry timer alive only while
 * there is real work to do (a tablet sitting online costs nothing).
 */
function hasPendingMoments(): boolean {
  return readLocalPendingStartMs() > 0 || readLocalPendingCloseMs() > 0;
}

/**
 * The in-flight flush of everything owed to the API, or null when idle.
 */
let pendingMomentsFlush: Promise<void> | null = null;

/**
 * Delivers every moment that could not be sent while the tablet was offline.
 *
 * Ordered CLOSE first, then START, so anything owed from earlier sessions reaches the
 * API before `trackDeviceStart` reports THIS launch. That order is what keeps the
 * backend's single `last_start` / `last_closed` pair per device correct: an older open
 * replayed after this session's own open would overwrite it with a stale time.
 * Both replays are idempotent (each clears its own stored copy only after the server
 * confirms), so running this on boot, on `online` and on a timer can never double-report.
 *
 * Serialised through {@link pendingMomentsFlush}: the triggers overlap heavily (boot +
 * `online` + `networkStatusChange` + the retry timer can all fire within the same second
 * while a tablet reconnects), and a caller such as `trackDeviceStart` gets to wait for a
 * flush that is already running instead of racing past it.
 */
function flushPendingMoments(): Promise<void> {
  if (pendingMomentsFlush) return pendingMomentsFlush;

  pendingMomentsFlush = (async () => {
    try {
      // Only ANDROID attendance-tablet devices are ever tracked (same rule as every
      // other entry point), so another platform / role can not drag a stored moment out.
      if (!isDeviceTrackingAllowed()) return;
      await flushPendingCloseOnce();
      await flushPendingStart();
      // While something is still owed the next tick / online event keeps trying; the
      // failure itself is logged by performSend, this is the housekeeping.
      if (hasPendingMoments()) scheduleOfflineFlush();
      else clearOfflineFlushTimer();
    } finally {
      pendingMomentsFlush = null;
    }
  })();

  return pendingMomentsFlush;
}

function clearOfflineFlushTimer(): void {
  if (retryTimer !== null) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
}

/**
 * Queues a deferred flush of everything still owed. Idempotent: at most one timer is
 * ever pending, no matter how many starts / closes fail while the tablet is offline.
 */
function scheduleOfflineFlush(delayMs = RETRY_INTERVAL_MS): void {
  clearOfflineFlushTimer();
  retryTimer = setTimeout(() => {
    retryTimer = null;
    void flushPendingMoments();
  }, delayMs);
}

/**
 * Reports the moment the attendance device / app was opened — a real process launch
 * (a killed app started by the user or by the schedule). Anything still owed from the
 * previous session is flushed first, each with its original time; the native report
 * context is refreshed inside `requestDeviceTracking` only when the start is really sent.
 *
 * After the flush the native arm state is synced: a NON-attendance session (other role,
 * logged out) disarms native so it records nothing (see {@link syncNativeTrackingContext});
 * an attendance session arms native through its start below. The flush always runs FIRST
 * so an owed attendance close can still be reported (natively or by the role-gated JS
 * flush) before the arm state changes.
 *
 * The stored moments are replayed BEFORE this launch's own start: the backend keeps a
 * single `last_start` per device, so the older open has to reach it first or this
 * launch would overwrite it.
 */
export const trackDeviceStart = (): Promise<void> =>
  flushPendingMoments().then(() => {
    void syncNativeTrackingContext();
    // A page REFRESH (pull-to-refresh, version update, chunk recovery) replaced this
    // document but the process never died, so the open was already reported by the
    // document we just replaced. The gate has to run AFTER the flush above: flushing an
    // owed close leaves `lastSentState === "close"`, which would otherwise let a bogus
    // start through and overwrite `last_start` with the refresh time.
    if (isRefreshContinuation()) {
      // Reset a state left behind by the flush (an owed close from an EARLIER session
      // sets `lastSentState = "close"`), so this session's own close is not mistaken for
      // a duplicate. Deliberately skipped once `sessionClosed` is set — that is a LIVE
      // close of THIS session, and overwriting it would un-gate a second close POST.
      if (!sessionClosed) lastSentState = "start";
      return;
    }
    return requestDeviceTracking("start");
  });

/**
 * Reports the moment the attendance device / app was closed
 * (manual exit / restart or a schedule driven auto close) — never a background state or
 * a device lock while the app stays open.
 */
export const trackDeviceClose = (): Promise<void> =>
  requestDeviceTracking("close");

/**
 * Awaits the close report (bounded) so the request can finish before the app closes.
 * Used by the in-app Exit / Restart actions — the process is killed right after.
 */
export function trackDeviceCloseAndWait(maxWaitMs = 2500): Promise<void> {
  return Promise.race([
    trackDeviceClose(),
    new Promise<void>((resolve) => setTimeout(resolve, maxWaitMs)),
  ]);
}

let initCleanup: (() => void) | null = null;

/**
 * Starts the JS side of device tracking (idempotent). Returns the teardown function.
 * Mounted once from `App` — see `DeviceTrackingHandler`.
 *
 * Sources of a "start" (a real app open — a fresh process launch):
 *   - the initial call below (this JavaScript booting = the process was launched),
 *   - the schedule (re)opening the app (`autoLaunched`),
 *   - the tablet user logging in (handled by `DeviceTrackingHandler`).
 * Sources of a "close" (the process really ending):
 *   - the schedule about to kill the process (`aboutToClose`),
 *   - the webview being torn down (`pagehide`),
 *   - an explicit `trackDeviceClose()` from the in-app Exit / Restart action,
 *   - a termination detected natively (reported on the next launch).
 *
 * There is deliberately NO `appStateChange` handling: a foreground / background change or
 * a device lock is neither an open nor a close. A background that the app never survives
 * (force stop, OOM kill, reboot) is covered natively: the tentative
 * close stored on the background transition is flushed here on the next launch.
 *
 * Duplicates can not occur across the layers: `lastSentState` / `sessionClosed` gate the
 * signals inside this session, `noteCloseSending` + the native-first gate stop the WebView
 * and the native hook from both POSTing one close, and the flush checks the JS *and*
 * native "last reported" markers before replaying anything.
 */
export function initDeviceTracking(): () => void {
  if (initCleanup) return initCleanup;

  // Android ONLY: on web and iOS no listener is registered and no start is reported,
  // so those builds can never write to the tracking data (the feature — auto close,
  // native close hook, kiosk deployment — exists on Android and nothing else).
  if (!isDeviceTrackingPlatform()) {
    console.log(
      "[DeviceTracking] Android only - tracker idle on this platform",
    );
    return () => undefined;
  }

  const disposers: (() => void)[] = [];

  // The schedule (re)opened the app — a real launch of a killed process.
  const handleAutoLaunched = () => void trackDeviceStart();
  document.addEventListener(APP_AUTO_LAUNCHED_EVENT, handleAutoLaunched);
  disposers.push(() =>
    document.removeEventListener(APP_AUTO_LAUNCHED_EVENT, handleAutoLaunched),
  );

  // The auto close scheduler is about to kill the process — report it during the grace
  // period (the pending copy is kept as well, in case the request cannot complete).
  const handleAboutToClose = () => void trackDeviceClose();
  document.addEventListener(APP_ABOUT_TO_CLOSE_EVENT, handleAboutToClose);
  disposers.push(() =>
    document.removeEventListener(APP_ABOUT_TO_CLOSE_EVENT, handleAboutToClose),
  );

  // The webview being torn down = the app really closed (a manual exit / restart or an OS
  // kill). This does NOT fire when the app is merely backgrounded or the device locked.
  //
  // EXCEPTION — a page REFRESH (pull-to-refresh, `location.reload()`): `pagehide` fires
  // there too, but the process is NOT dying, so reporting a close would invent an app
  // close that never happened. `markPageRefresh()` writes the marker immediately before
  // every reload in this app; it is read (not consumed) here so the incoming document
  // can still see it and skip the matching false start.
  const handlePageHide = (event: PageTransitionEvent) => {
    if (event.persisted) return;
    if (isPageRefreshInFlight()) {
      console.log(
        "[DeviceTracking] pagehide from a page refresh - not an app close, nothing reported",
      );
      return;
    }
    void trackDeviceClose();
  };
  window.addEventListener("pagehide", handlePageHide);
  disposers.push(() => window.removeEventListener("pagehide", handlePageHide));

  // ---------------------------------------------------------------------------------
  // Connectivity recovery — the "no internet at close AND at launch, network later on"
  // case. Every moment that could not be delivered is stored (see LS_PENDING_*), and
  // these triggers replay it with its ORIGINAL time. All of them are idempotent and
  // serialised through `flushPendingMoments`, so overlapping events are harmless.
  // ---------------------------------------------------------------------------------

  // The DOM `online` event: fires in a desktop browser and whenever the Android WebView
  // is told the network changed.
  const handleOnline = () => {
    console.log(
      "[DeviceTracking] Back online - delivering the moments missed offline",
    );
    void flushPendingMoments();
  };
  window.addEventListener("online", handleOnline);
  disposers.push(() => window.removeEventListener("online", handleOnline));

  // Capacitor's Network plugin is the RELIABLE signal on a native device: it is driven
  // by the OS connectivity callback, so it fires even when the WebView was frozen while
  // the tablet was in the background and the radio reconnected (the common case for a
  // wall-mounted attendance tablet that nobody touches).
  let removeNetworkListener: (() => void) | null = null;
  if (Capacitor.isNativePlatform()) {
    void Network.addListener("networkStatusChange", (status) => {
      if (!status?.connected) return;
      console.log(
        "[DeviceTracking] Connectivity restored - delivering the moments missed offline",
      );
      void flushPendingMoments();
    })
      .then((handle) => {
        // Remember the handle so the listener is removed on teardown; until then the
        // disposer is a no-op (init is idempotent and runs once per process anyway).
        removeNetworkListener = () => void handle.remove();
      })
      .catch((error) =>
        console.warn("[DeviceTracking] network listener unavailable:", error),
      );
  }

  // This JavaScript booting means the app was launched from a killed state: record the
  // open moment now (moments left over from a previous, offline session are flushed
  // first by trackDeviceStart). A REFRESH does not report one — that check lives inside
  // trackDeviceStart, after the flush, so an owed close can not un-gate it.
  void trackDeviceStart();

  initCleanup = () => {
    clearOfflineFlushTimer();
    if (removeNetworkListener) removeNetworkListener();
    disposers.forEach((dispose) => {
      try {
        dispose();
      } catch {
        /* listener already gone */
      }
    });
    initCleanup = null;
  };

  return initCleanup;
}
